import { SupabaseClient } from '@supabase/supabase-js';

/**
 * Expert Priority Query
 * 
 * Membership tier multipliers are baked into global_rank by calculate-expert-rankings.
 * All queries simply sort by global_rank ASC — no multi-tier waterfall needed.
 * 
 * IMPORTANT: Uses users_profiles view (public-safe, no PII) for anonymous access.
 */

const EXPERT_SELECT_FIELDS = 'id, display_name, business, country, image, vanity_url, membership_tier, profile_verified, about, global_rank, location_id';

interface RankedExpertRow {
  id: string;
  display_name: string | null;
  business: string | null;
  country: string | null;
  image: string | null;
  vanity_url: string | null;
  membership_tier: string | null;
  profile_verified?: boolean | null;
  about?: string | null;
  global_rank: number | null;
  location_id: string | null;
}

export interface ExpertQueryOptions {
  locationId: string;
  limit?: number;
  selectFields?: string;
  includeGlobalFallback?: boolean;
}

/**
 * Fetch experts by location, ordered by global_rank (which already includes membership boost).
 * Falls back to global experts if not enough local ones.
 */
export async function fetchExpertsByLocationWithPriority(
  supabase: SupabaseClient,
  options: ExpertQueryOptions
): Promise<unknown[]> {
  const { locationId, limit = 14, selectFields = EXPERT_SELECT_FIELDS, includeGlobalFallback = true } = options;

  const { data: localExperts } = await supabase
    .from('users_profiles')
    .select(selectFields)
    .eq('location_id', locationId)
    .eq('has_email', true)
    .eq('has_valid_vanity', true)
    .order('global_rank', { ascending: true, nullsFirst: false })
    .limit(limit);

  const localCount = localExperts?.length || 0;
  if (localCount >= limit || !includeGlobalFallback) return localExperts || [];

  const { data: globalExperts } = await supabase
    .from('users_profiles')
    .select(selectFields)
    .neq('location_id', locationId)
    .eq('has_email', true)
    .eq('has_valid_vanity', true)
    .order('global_rank', { ascending: true, nullsFirst: false })
    .limit(limit - localCount);

  return [...(localExperts || []), ...(globalExperts || [])];
}

/**
 * Fetch global experts with one expert per location first, then fill remaining slots.
 */
export async function fetchGlobalExpertsWithLocationDiversity(
  supabase: SupabaseClient,
  limit: number = 10,
  selectFields: string = EXPERT_SELECT_FIELDS
): Promise<unknown[]> {
  const fetchLimit = Math.max(limit * 10, 100);

  const { data } = await supabase
    .from('users_profiles')
    .select(selectFields)
    .eq('has_email', true)
    .eq('has_valid_vanity', true)
    .order('global_rank', { ascending: true, nullsFirst: false })
    .limit(fetchLimit);

  const experts = Array.isArray(data)
    ? (data as unknown as RankedExpertRow[])
    : [];
  const seenLocations = new Set<string>();
  const uniqueByLocation: RankedExpertRow[] = [];
  const remainder: RankedExpertRow[] = [];

  for (const expert of experts) {
    const locationKey = expert.location_id;

    if (locationKey && !seenLocations.has(locationKey)) {
      seenLocations.add(locationKey);
      uniqueByLocation.push(expert);
      continue;
    }

    remainder.push(expert);
  }

  return [...uniqueByLocation, ...remainder].slice(0, limit);
}

/**
 * Build the OR clause for expert filtering (for use in existing queries)
 */
export function buildExpertPriorityFilter(): string {
  return 'profile_listed.eq.true';
}

/**
 * Get single top expert from a location — just the #1 by global_rank.
 */
export async function getTopExpertByLocation(
  supabase: SupabaseClient,
  locationId: string,
  selectFields: string = EXPERT_SELECT_FIELDS
): Promise<unknown | null> {
  const { data } = await supabase
    .from('users_profiles')
    .select(selectFields)
    .eq('location_id', locationId)
    .eq('has_email', true)
    .eq('has_valid_vanity', true)
    .order('global_rank', { ascending: true, nullsFirst: false })
    .limit(1)
    .maybeSingle();

  return data;
}
